import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, NavList, Search } from '@enamelai/design-system'
import { toSentenceCase } from '@utils/textCase.util'
import {
  getFirstBundleReportPath,
  REPORTS_ENGAGEMENT_SUBMENU,
  REPORTS_FINANCE_SUBMENU,
  REPORTS_OPERATIONS_SUBMENU,
  REPORTS_PATIENTS_SUBMENU,
  REPORTS_PROVIDER_PERFORMANCE_SUBMENU,
  REPORTS_SIDEBAR_BUNDLE_ITEMS,
} from '@constants/Reports/reportsPage.constants'

type ReportSubmenuItem = {
  id: string
  label: string
  pathSegment: string
}

const SUBMENUS_BY_BUNDLE: Record<string, ReportSubmenuItem[]> = {
  patients: REPORTS_PATIENTS_SUBMENU,
  engagement: REPORTS_ENGAGEMENT_SUBMENU,
  operations: REPORTS_OPERATIONS_SUBMENU,
  'provider-performance': REPORTS_PROVIDER_PERFORMANCE_SUBMENU,
  finance: REPORTS_FINANCE_SUBMENU,
}

function getActiveBundleId(pathname: string): string | undefined {
  return /^\/report\/bundles\/([^/]+)/.exec(pathname)?.[1]
}

function getActiveReportId(pathname: string): string | undefined {
  return /^\/report\/bundles\/[^/]+\/([^/]+)/.exec(pathname)?.[1]
}

type ReportsSidebarProps = {
  searchQuery: string
  onSearchChange: (query: string) => void
}

const ReportsSidebar = ({
  searchQuery,
  onSearchChange,
}: ReportsSidebarProps): React.ReactElement => {
  const location = useLocation()
  const navigate = useNavigate()
  const activeBundleId = getActiveBundleId(location.pathname)
  const activeReportId = getActiveReportId(location.pathname)
  const activeSubmenu = activeBundleId
    ? SUBMENUS_BY_BUNDLE[activeBundleId]
    : undefined

  return (
    <aside className="reports-side-panel-wrapper" aria-label="Reports">
      <div className="reports-side-panel">
        <div className="reports-side-panel-heading">
          <span className="reports-title">Reports</span>
          <div className="reports-side-panel-search">
            <Search
              placeholder="Search"
              value={searchQuery}
              onChange={onSearchChange}
              onClear={() => onSearchChange('')}
              ariaLabel="Search reports"
            />
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          fullWidth
          className={`reports-sidebar-parent${
            location.pathname === '/report' ? ' active' : ''
          }`}
          onClick={() => navigate('/report')}
        >
          Bundles
        </Button>

        <div className="reports-sidebar-bundles">
          {REPORTS_SIDEBAR_BUNDLE_ITEMS.map((item) => {
            const isExpanded = activeBundleId === item.id

            return (
              <div
                key={item.id}
                className={`reports-sidebar-bundle-group${
                  isExpanded ? ' reports-sidebar-bundle-group--expanded' : ''
                }`}
              >
                <NavList
                  items={[
                    {
                      id: item.id,
                      label: toSentenceCase(item.label),
                    },
                  ]}
                  value={isExpanded ? item.id : ''}
                  onChange={() => {
                    const destination = getFirstBundleReportPath(item.id)
                    if (destination) navigate(destination)
                  }}
                  className="reports-sidebar-bundle"
                />

                {isExpanded && activeSubmenu ? (
                  <NavList
                    items={activeSubmenu.map((submenuItem) => ({
                      id: submenuItem.pathSegment,
                      label: toSentenceCase(submenuItem.label),
                    }))}
                    value={activeReportId ?? ''}
                    showChevron={false}
                    onChange={(reportId) =>
                      navigate(`/report/bundles/${item.id}/${reportId}`)
                    }
                    className="reports-sidebar-reports"
                  />
                ) : null}
              </div>
            )
          })}
        </div>

        <NavList
          items={[
            { id: 'custom', label: 'Custom reports' },
            { id: 'presets', label: 'Presets and automation' },
          ]}
          value={
            location.pathname.startsWith('/report/custom')
              ? 'custom'
              : location.pathname.startsWith('/report/presets')
                ? 'presets'
                : undefined
          }
          showChevron={false}
          onChange={(itemId) => navigate(`/report/${itemId}`)}
          className="reports-sidebar-secondary"
        />
      </div>
    </aside>
  )
}

export default ReportsSidebar
